import voca from 'voca';

type Valuable<T> = { [K in keyof T as T[K] extends null | undefined ? never : K]: T[K] };

export function removeFalsyValuesFromObject<
  // eslint-disable-next-line @typescript-eslint/ban-types
  T extends {},
  V = Valuable<T>,
>(obj: T): V {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => !((typeof v === 'string' && !v.length) || v === null || typeof v === 'undefined'),
    ),
  ) as V;
}

export const convertToTitleCase = (text: string) => {
  const filteredText = text.replaceAll(/[_-]/g, ' ');
  return voca.titleCase(filteredText);
};

export function isBaseString(base: string, other: string): boolean {
  // Ensure both strings start with a slash to match URL paths
  if (!base.startsWith('/') || !other.startsWith('/')) {
    return false;
  }

  // Remove trailing slashes from both strings
  base = base.replace(/\/+$/, '');
  other = other.replace(/\/+$/, '');

  return other.startsWith(base);
}

export const toSnakeCaseKeys: any = (input: any) => {
  if (Array.isArray(input) && input.length === 0) return [];

  if (Array.isArray(input) && typeof input[0] === 'object') {
    return input.map((item) => toSnakeCaseKeys(item));
  }
  if (typeof input === 'object' && input !== null) {
    return Object.keys(input).reduce((acc: any, key) => {
      const snakeCaseKey = voca.snakeCase(key);
      acc[snakeCaseKey as keyof {}] = toSnakeCaseKeys(input[key]);
      return acc;
    }, {});
  }
  return input;
};

export const toCamelCaseKeys: any = (input: any) => {
  if (Array.isArray(input) && input.length === 0) return [];

  if (Array.isArray(input) && typeof input[0] === 'object') {
    return input.map((item) => toCamelCaseKeys(item));
  }
  if (typeof input === 'object' && input !== null) {
    return Object.keys(input).reduce((acc: any, key) => {
      const snakeCaseKey = voca.camelCase(key);
      acc[snakeCaseKey as keyof {}] = toCamelCaseKeys(input[key]);
      return acc;
    }, {});
  }
  return input;
};

export const isArrayOfObjects = <T>(
  arr: T[],
): arr is Exclude<T, null | undefined | string | number | symbol>[] =>
  arr.every((item) => typeof item === 'object' && item !== null && !Array.isArray(item));

export const orderCodeSplitter = (orderCode: string) => {
  const numbersArray = orderCode
    .split(/[^0-9]+/)
    .filter(Boolean)
    .map(Number);
  return numbersArray;
};

export const stringifyQueryParams = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (typeof value === 'object' && !Array.isArray(value)) {
      // If object, stringify it
      searchParams.set(key, JSON.stringify(value));
    } else if (Array.isArray(value)) {
      // If array, join or set multiple values
      value.forEach((v) => searchParams.append(key, String(v)));
    } else {
      searchParams.set(key, String(value));
    }
  });

  return searchParams.toString();
};

export const capitalizeFirstLetter = (str: string) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export function cleanPhoneNumber(phone: string | null): string | null {
  if (!phone) return '';

  // Remove all whitespace and dots
  const cleaned = phone
    .toString()
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '') // Remove invisible Unicode characters [These can be there due to copy pasting the phone number]
    .replace(/[\s.]+/g, '')
    .trim();

  // Only remove +39 if it appears at the beginning
  return cleaned.startsWith('+39') ? cleaned.slice(3) : cleaned;
}

/**
 * Normalize a string by:
 * - Removing accents/diacritics (e.g., "é" → "e", "ñ" → "n")
 * - Converting it to lowercase
 * - Returning an empty string if input is not a valid string
 */
export const normalizeStr = (str: unknown): string => {
  // Ensure the input is a string before processing
  if (typeof str !== 'string') return '';

  // Normalize the string to NFD (Normalization Form Decomposition)
  // This separates base characters from their diacritical marks
  return str
    .trim()
    .normalize('NFD') // e.g., "é" becomes "é" (e + combining accent)
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .toLowerCase(); // Convert to lowercase for case-insensitive comparison
};

/**
 * Encodes a JavaScript object into a URL-safe base64 string.
 * This can be useful for passing complex data in query parameters.
 *
 * @param payload - The object to encode.
 * @returns A base64-encoded and URI-encoded string.
 */
export const encodePayload = (payload: object): string => {
  // Convert the object to a JSON string
  const json = JSON.stringify(payload);

  // Encode the JSON string to make it URI-safe, then base64 encode it
  return btoa(encodeURIComponent(json));
};

/**
 * Decodes a base64 + URI-encoded string back into a JavaScript object.
 *
 * @param encoded - The encoded string to decode.
 * @returns The original object, or null if decoding fails.
 */
export const decodePayload = <T>(encoded: string | null): T | null => {
  // Return null if the input is empty
  if (!encoded) return null;

  try {
    // Decode from base64, then decode URI components
    const json = decodeURIComponent(atob(encoded));

    // Parse the JSON string back into an object
    return JSON.parse(json) as T;
  } catch (e) {
    // Handle any decoding/parsing errors gracefully
    console.error('Failed to decode payload', e);
    return null;
  }
};
